import React, { useState, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import { toast } from "sonner";
import {
  Film, Users, Sparkles, ArrowRight, ArrowLeft, Loader2, CheckCircle2, Play, Download, Share2, X,
  Image as ImageIcon, Upload, Trash2, RotateCw, Rocket, Wand2,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { StoryAssetsPanel } from "../components/studio/StoryAssetsPanel";
import { ReferenceUploader } from "../components/studio/ReferenceUploader";
import { UgcCreateButton } from "../components/studio/UgcCreatePanel";
import { BatchButton } from "../components/studio/BatchPanel";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PLATFORMS = [
  { key: "instagram", label: "Instagram Reels" },
  { key: "tiktok", label: "TikTok" },
  { key: "youtube_shorts", label: "YouTube Shorts" },
];
const LANGUAGES = ["english", "hindi", "tamil", "spanish", "french", "german", "portuguese", "arabic", "mandarin"];
const STYLES = [
  { key: "cinematic_dialogue", label: "Character speaks (Cinematic dialogue)" },
  { key: "documentary_cinematic", label: "Voice-over (Documentary cinematic)" },
  { key: "influencer_studio", label: "Influencer studio (Talking-head)" },
];

const EMPTY_WIZARD = {
  influencer: null,
  brand_id: "",
  brief: "",
  attachments: [], // [{id, url, name, kind, t_sec}]
  platform: "instagram",
  duration_target_sec: 45,
  language: "english",
  style_preset: "cinematic_dialogue",
  personalized_name: "",
};

// Server-saved wizard draft: restores after refresh/re-login, autosaves (debounced), one per user.
function useWizardDraft(state, setState, step, setStep) {
  const [draftId, setDraftId] = React.useState(null);
  const [loaded, setLoaded] = React.useState(false);
  const [draftStatus, setDraftStatus] = React.useState("");
  const skipSave = React.useRef(false);
  React.useEffect(() => {
    api.get("/studio/wizard/draft").then(({ data }) => {
      const d = data?.draft;
      if (d) {
        setDraftId(d.id);
        setState({ ...EMPTY_WIZARD, ...Object.fromEntries(Object.keys(EMPTY_WIZARD).map((k) => [k, d[k] ?? EMPTY_WIZARD[k]])) });
        if (d.step) setStep(d.step);
        skipSave.current = true; // restoring isn't an edit — don't immediately re-save
        setDraftStatus("Draft restored");
      }
    }).catch(() => {}).finally(() => setLoaded(true));
  }, [setState, setStep]);
  React.useEffect(() => {
    if (!loaded) return;
    if (skipSave.current) { skipSave.current = false; return; }
    const empty = !state.influencer && !state.brief && state.attachments.length === 0;
    if (empty && !draftId) return;
    setDraftStatus("Saving…");
    const t = setTimeout(() => {
      const inf = state.influencer && { id: state.influencer.id, name: state.influencer.name, archetype: state.influencer.archetype, avatar_url: state.influencer.avatar_url };
      api.put("/studio/wizard/draft", { ...state, influencer: inf, step })
        .then(({ data }) => { setDraftId(data.draft_id); setDraftStatus("Draft saved"); })
        .catch(() => setDraftStatus("Not saved — will retry on next change"));
    }, 700);
    return () => clearTimeout(t);
  }, [state, step, loaded, draftId]);
  const resetDraft = (discard = true) => {
    if (discard) api.delete("/studio/wizard/draft").catch(() => {});
    setDraftId(null);
    setDraftStatus("");
    setState(EMPTY_WIZARD);
    setStep(1);
  };
  return { draftId, draftStatus, resetDraft };
}

export default function Studio() {
  const qc = useQueryClient();
  const [step, setStep] = useState(1); // 1..3
  const [selected, setSelected] = useState(null); // ContentPiece detail view
  const [state, setState] = useState(EMPTY_WIZARD);
  const [generating, setGenerating] = useState(false);
  const { draftId, draftStatus, resetDraft } = useWizardDraft(state, setState, step, setStep);

  const { data: brands } = useQuery({
    queryKey: ["brands"],
    queryFn: () => api.get("/brands").then((r) => r.data),
  });
  const { data: content } = useQuery({
    queryKey: ["content"],
    queryFn: () => api.get("/studio/content").then((r) => r.data),
    refetchInterval: 4500,
  });

  // Default brand_id
  React.useEffect(() => {
    if (!state.brand_id && brands?.length) setState((s) => ({ ...s, brand_id: brands[0].id }));
  }, [brands, state.brand_id]);

  const canGoNext = useMemo(() => {
    if (step === 1) return !!state.influencer;
    if (step === 2) return state.brief.trim().length >= 15 && !!state.brand_id;
    return true;
  }, [step, state]);

  const generate = async () => {
    setGenerating(true);
    try {
      const { data } = await api.post("/studio/wizard/generate", {
        draft_id: draftId || undefined,
        brand_id: state.brand_id,
        influencer_id: state.influencer.id,
        brief: state.brief.trim(),
        attachments: state.attachments.map((a) => ({ id: a.id, kind: a.kind || "reference" })),
        platform: state.platform,
        duration_target_sec: state.duration_target_sec,
        language: state.language,
        style_preset: state.style_preset,
        personalized_name: state.personalized_name.trim() || undefined,
      });
      const c = data?.content;
      toast.success(data?.reused ? `Opened existing storyboard — ${c.title}` : `Storyboard ready — ${c.title}`);
      qc.invalidateQueries({ queryKey: ["content"] });
      resetDraft(false);
      setSelected(c);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Generation failed");
    } finally {
      setGenerating(false);
    }
  };

  const createEduDemo = async () => {
    try {
      const { data } = await api.post("/studio/edu/fixtures/cbsetutor");
      toast.success("Draft created — 4 diagram scenes · 30s · nova narration");
      qc.invalidateQueries({ queryKey: ["content"] });
      setSelected(data.content);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create demo draft");
    }
  };

  const createStoryDraft = async () => {
    try {
      const { data } = await api.post("/studio/story/fixtures/one-question");
      toast.success(data.reused ? "Opened existing story draft" : "Story film draft created — 6 scenes · 30s");
      qc.invalidateQueries({ queryKey: ["content"] });
      setSelected(data.content);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create story draft");
    }
  };

  if (selected) {
    return <ReelDetail content={selected} onBack={() => setSelected(null)} qc={qc} />;
  }

  return (
    <div className="min-h-screen bg-white text-slate-900">
      <div className="border-b border-slate-200 px-8 py-5 flex items-end justify-between">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-slate-500">// Creative Studio</div>
          <h1 className="font-display font-bold text-2xl mt-1">Make a reel in 3 steps.</h1>
        </div>
        <div className="flex items-center gap-4">
          {draftStatus && <span data-testid="draft-status" className="text-[11px] font-mono text-slate-500">{draftStatus}</span>}
          {draftId && (
            <button data-testid="draft-reset" onClick={() => window.confirm("Discard this draft and start over?") && resetDraft(true)} className="text-[11px] text-slate-500 hover:text-red-600 underline">
              Start over
            </button>
          )}
          <StepPips step={step} />
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12">
        {/* Main wizard */}
        <div className="lg:col-span-8 border-r border-slate-100 p-8 min-h-[calc(100vh-88px)]">
          {step === 1 && <Step1PickInfluencer state={state} setState={setState} />}
          {step === 2 && <Step2Brief state={state} setState={setState} brands={brands || []} />}
          {step === 3 && <Step3Review state={state} onGenerate={generate} generating={generating} />}

          <div className="mt-8 pt-6 border-t border-slate-100 flex items-center justify-between">
            {step > 1 ? (
              <Button data-testid="wiz-back" variant="ghost" onClick={() => setStep((s) => s - 1)} className="text-slate-600 hover:text-slate-900">
                <ArrowLeft className="w-4 h-4 mr-2" /> Back
              </Button>
            ) : <div />}
            {step < 3 ? (
              <Button data-testid="wiz-next" onClick={() => setStep((s) => s + 1)} disabled={!canGoNext} className="bg-blue-600 hover:bg-blue-700 text-white rounded-md h-10 px-6">
                Continue <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            ) : (
              <Button data-testid="wiz-generate" onClick={generate} disabled={generating} className="bg-blue-600 hover:bg-blue-700 text-white rounded-md h-11 px-6">
                {generating ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Building storyboard…</> : <><Wand2 className="w-4 h-4 mr-2" />Build storyboard</>}
              </Button>
            )}
          </div>
        </div>

        {/* Recent reels library */}
        <div className="lg:col-span-4 bg-slate-50 p-6 overflow-y-auto max-h-[calc(100vh-88px)]">
          <div className="flex items-center justify-between mb-3">
            <div className="font-mono text-[10px] uppercase tracking-widest text-slate-500">// Your reels</div>
            <Button
              data-testid="create-edu-demo-btn"
              variant="outline"
              size="sm"
              onClick={createEduDemo}
              className="h-7 text-xs"
            >
              + Maths diagram demo
            </Button>
          </div>
          <UgcCreateButton onCreated={(c) => { qc.invalidateQueries({ queryKey: ["content"] }); setSelected(c); }} />
          <BatchButton onOpenContent={setSelected} />
          <Button
            data-testid="create-story-draft-btn"
            variant="outline"
            size="sm"
            onClick={createStoryDraft}
            className="h-7 text-xs mb-3 w-full"
          >
            + Story film draft · "One question at a time"
          </Button>
          <div className="space-y-3">
            {(content || []).slice(0, 20).map((c) => (
              <button
                key={c.id}
                data-testid={`library-${c.id}`}
                onClick={() => setSelected(c)}
                className="w-full text-left bg-white border border-slate-200 rounded-lg overflow-hidden hover:border-blue-300 hover:shadow-sm transition-all"
              >
                <div className="aspect-video bg-slate-100 relative">
                  {c.thumbnail_url ? (
                    <img src={c.thumbnail_url} alt={c.title} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-slate-300"><Film className="w-6 h-6" /></div>
                  )}
                  <StatusPill status={c.video_render_status} />
                </div>
                <div className="p-3">
                  <div className="font-medium text-sm text-slate-900 line-clamp-1">{c.title}</div>
                  <div className="font-mono text-[9px] uppercase tracking-widest text-slate-400 mt-1">
                    {c.duration_target_sec}s · {c.platform} · {c.language || "en"}
                  </div>
                </div>
              </button>
            ))}
            {(!content || content.length === 0) && (
              <div className="text-center py-8 text-sm text-slate-400">
                No reels yet. Complete step 3 and build your first one.
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function StepPips({ step }) {
  return (
    <div className="flex items-center gap-2">
      {[1, 2, 3].map((n) => (
        <React.Fragment key={n}>
          <div data-testid={`step-pip-${n}`} className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs ${
            step === n ? "bg-blue-600 text-white font-semibold" : step > n ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"
          }`}>
            <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold ${
              step === n ? "bg-white text-blue-600" : step > n ? "bg-emerald-600 text-white" : "bg-slate-200 text-slate-500"
            }`}>
              {step > n ? "✓" : n}
            </span>
            {["Pick influencer", "Describe & attach", "Review & render"][n - 1]}
          </div>
          {n < 3 && <div className="w-6 h-px bg-slate-200" />}
        </React.Fragment>
      ))}
    </div>
  );
}

function StatusPill({ status }) {
  const cfg = {
    rendering: { c: "bg-blue-100 text-blue-800 border-blue-300", l: "RENDERING" },
    ready: { c: "bg-emerald-100 text-emerald-800 border-emerald-300", l: "READY" },
    failed: { c: "bg-red-100 text-red-700 border-red-300", l: "FAILED" },
    idle: { c: "bg-slate-100 text-slate-600 border-slate-300", l: "DRAFT" },
  };
  const key = ["rendering", "ready", "failed"].includes(status) ? status : "idle";
  const { c, l } = cfg[key];
  return <div className={`absolute top-2 right-2 font-mono text-[9px] uppercase tracking-widest px-2 py-0.5 rounded border ${c} bg-opacity-90 backdrop-blur`}>{l}</div>;
}

/* ============ STEP 1 ============ */
function Step1PickInfluencer({ state, setState }) {
  const { data: influencers } = useQuery({
    queryKey: ["influencers"],
    queryFn: () => api.get("/influencers").then((r) => r.data),
  });
  const list = influencers || [];
  return (
    <div>
      <div className="font-mono text-[10px] uppercase tracking-widest text-slate-500">Step 1</div>
      <h2 className="font-display font-bold text-xl mt-1 text-slate-900">Who's on camera?</h2>
      <p className="text-sm text-slate-500 mt-1">Pick from your roster. Consistent face across every scene.</p>

      {list.length === 0 ? (
        <div className="mt-6 rounded-xl border-2 border-dashed border-slate-200 p-10 text-center">
          <Users className="w-8 h-8 mx-auto mb-3 text-slate-300" />
          <div className="text-sm text-slate-600 font-medium">No influencers in your roster yet</div>
          <a href="/app/influencers" data-testid="empty-goto-influencers" className="inline-block mt-3 text-sm text-blue-600 hover:underline font-medium">
            Add one from the Library or Build custom → Influencers
          </a>
        </div>
      ) : (
        <div className="mt-5 grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3">
          {list.map((inf) => {
            const picked = state.influencer?.id === inf.id;
            return (
              <button
                key={inf.id}
                data-testid={`pick-inf-${inf.id}`}
                onClick={() => setState({ ...state, influencer: inf })}
                className={`text-left rounded-lg overflow-hidden border-2 transition-all ${
                  picked ? "border-blue-600 ring-2 ring-blue-200 shadow-md" : "border-slate-200 hover:border-blue-300"
                }`}
              >
                <div className="aspect-[4/5] bg-slate-100 overflow-hidden relative">
                  {inf.avatar_url ? (
                    <img src={inf.avatar_url} alt={inf.name} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-slate-300"><Users className="w-8 h-8" /></div>
                  )}
                  {picked && (
                    <div className="absolute inset-0 bg-blue-600/25 flex items-center justify-center">
                      <div className="w-9 h-9 rounded-full bg-blue-600 flex items-center justify-center shadow-lg">
                        <CheckCircle2 className="w-5 h-5 text-white" strokeWidth={2.5} />
                      </div>
                    </div>
                  )}
                </div>
                <div className="p-2.5 bg-white">
                  <div className="font-display font-semibold text-sm text-slate-900 truncate">{inf.name}</div>
                  <div className="text-[11px] text-slate-500 truncate">{inf.archetype}</div>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* ============ STEP 2 ============ */
function Step2Brief({ state, setState, brands }) {
  return (
    <div>
      <div className="font-mono text-[10px] uppercase tracking-widest text-slate-500">Step 2</div>
      <h2 className="font-display font-bold text-xl mt-1 text-slate-900">Tell us what to build.</h2>
      <p className="text-sm text-slate-500 mt-1">Freeform brief + optional visual references. Our writers use both.</p>

      {/* Brand */}
      <div className="mt-6">
        <Label className="text-xs uppercase font-mono tracking-widest text-slate-500">Brand</Label>
        <Select value={state.brand_id} onValueChange={(v) => setState({ ...state, brand_id: v })}>
          <SelectTrigger data-testid="wiz-brand" className="mt-1.5 h-10 border-slate-300 rounded-md bg-white">
            <SelectValue placeholder="Pick a brand…" />
          </SelectTrigger>
          <SelectContent className="bg-white rounded-md">
            {brands.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {/* Brief */}
      <div className="mt-4">
        <Label className="text-xs uppercase font-mono tracking-widest text-slate-500">Brief · what should this reel say?</Label>
        <Textarea
          data-testid="wiz-brief"
          value={state.brief}
          onChange={(e) => setState({ ...state, brief: e.target.value })}
          placeholder="A 30-second monsoon-launch reel for the new olive silk kurta. She's caught in unexpected rain in Delhi — the fabric behaves beautifully. Warm cinematic, honest, no marketing copy."
          rows={5}
          className="mt-1.5 border-slate-300 focus:border-blue-500 focus-visible:ring-2 focus-visible:ring-blue-200 rounded-md text-sm"
        />
        <div className="font-mono text-[9px] uppercase tracking-widest text-slate-400 mt-1">
          Minimum 15 characters · {state.brief.trim().length} written
        </div>
      </div>

      {/* Attachments */}
      <div className="mt-4">
        <Label htmlFor="wiz-file" className="text-xs uppercase font-mono tracking-widest text-slate-500">Product photos & references (optional)</Label>
        <ReferenceUploader
          attachments={state.attachments}
          onChange={(fn) => setState((s) => ({ ...s, attachments: fn(s.attachments) }))}
        />
      </div>

      {/* Config strip */}
      <div className="mt-5 grid grid-cols-2 md:grid-cols-4 gap-3">
        <div>
          <Label className="text-xs uppercase font-mono tracking-widest text-slate-500">Platform</Label>
          <Select value={state.platform} onValueChange={(v) => setState({ ...state, platform: v })}>
            <SelectTrigger data-testid="wiz-platform" className="mt-1.5 h-10 border-slate-300 rounded-md bg-white"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white rounded-md">
              {PLATFORMS.map((p) => <SelectItem key={p.key} value={p.key}>{p.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs uppercase font-mono tracking-widest text-slate-500">Duration (sec)</Label>
          <Input data-testid="wiz-duration" type="number" min={15} max={120} value={state.duration_target_sec}
            onChange={(e) => setState({ ...state, duration_target_sec: parseInt(e.target.value) || 45 })}
            className="mt-1.5 h-10 border-slate-300 rounded-md" />
        </div>
        <div>
          <Label className="text-xs uppercase font-mono tracking-widest text-slate-500">Language</Label>
          <Select value={state.language} onValueChange={(v) => setState({ ...state, language: v })}>
            <SelectTrigger data-testid="wiz-language" className="mt-1.5 h-10 border-slate-300 rounded-md bg-white"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white rounded-md max-h-64">
              {LANGUAGES.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs uppercase font-mono tracking-widest text-slate-500">Style</Label>
          <Select value={state.style_preset} onValueChange={(v) => setState({ ...state, style_preset: v })}>
            <SelectTrigger data-testid="wiz-style" className="mt-1.5 h-10 border-slate-300 rounded-md bg-white"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white rounded-md">
              {STYLES.map((s) => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Personalize */}
      <div className="mt-4">
        <Label className="text-xs uppercase font-mono tracking-widest text-slate-500">Personalize · recipient's name (optional)</Label>
        <Input
          data-testid="wiz-personalize"
          value={state.personalized_name}
          onChange={(e) => setState({ ...state, personalized_name: e.target.value })}
          placeholder="Aisha"
          className="mt-1.5 h-10 border-slate-300 rounded-md"
        />
        <div className="font-mono text-[9px] uppercase tracking-widest text-slate-400 mt-1">
          Bakes the name into scene 1/2 dialogue — perfect for greetings and welcome nudges.
        </div>
      </div>
    </div>
  );
}

/* ============ STEP 3 ============ */
function Step3Review({ state, onGenerate, generating }) {
  return (
    <div>
      <div className="font-mono text-[10px] uppercase tracking-widest text-slate-500">Step 3</div>
      <h2 className="font-display font-bold text-xl mt-1 text-slate-900">Review, then build the storyboard.</h2>
      <p className="text-sm text-slate-500 mt-1">Claude writes 5 scenes with dialogue. Nano Banana renders storyboard stills. No credits spent yet — the render (Fal Seedance Pro) happens on the next screen.</p>

      <div className="mt-5 rounded-xl border border-slate-200 divide-y divide-slate-100">
        <ReviewRow icon={Users} label="Influencer">
          <div className="flex items-center gap-2.5">
            {state.influencer?.avatar_url && (
              <img src={state.influencer.avatar_url} alt={state.influencer.name} className="w-9 h-9 rounded-full object-cover border border-slate-200" />
            )}
            <div>
              <div className="text-sm font-medium text-slate-900">{state.influencer?.name}</div>
              <div className="text-[11px] text-slate-500">{state.influencer?.archetype}</div>
            </div>
          </div>
        </ReviewRow>
        <ReviewRow label="Brief">
          <div className="text-sm text-slate-700 whitespace-pre-wrap">{state.brief}</div>
        </ReviewRow>
        {state.attachments.length > 0 && (
          <ReviewRow icon={ImageIcon} label={`${state.attachments.length} image(s) · ${state.attachments.filter((a) => a.kind === "product" || a.kind === "charger").length} genuine product photo(s)`}>
            <div className="flex items-center gap-2 flex-wrap">
              {state.attachments.map((a) => (
                <img key={a.id} src={`${BACKEND_URL}${a.url}`} alt={a.name} title={a.kind} className="w-14 h-14 rounded-md object-cover border border-slate-200" />
              ))}
            </div>
          </ReviewRow>
        )}
        <ReviewRow label="Config">
          <div className="flex items-center gap-1.5 flex-wrap">
            <Chip>{state.platform}</Chip>
            <Chip>{state.duration_target_sec}s</Chip>
            <Chip>{state.language}</Chip>
            <Chip>{state.style_preset.replace(/_/g, " ")}</Chip>
            {state.personalized_name && <Chip>for {state.personalized_name}</Chip>}
          </div>
        </ReviewRow>
      </div>

      <div className="mt-4 rounded-lg border border-blue-100 bg-blue-50 p-3.5 flex items-start gap-2.5">
        <Sparkles className="w-4 h-4 text-blue-600 shrink-0 mt-0.5" />
        <div className="text-xs text-blue-900">
          <span className="font-semibold">Free storyboard.</span> You'll only be charged tokens when you kick off the Fal Seedance Pro video render on the next screen.
        </div>
      </div>
    </div>
  );
}

function ReviewRow({ icon: Icon, label, children }) {
  return (
    <div className="px-4 py-3 flex items-start gap-3">
      <div className="w-24 shrink-0 font-mono text-[10px] uppercase tracking-widest text-slate-500 pt-0.5">{label}</div>
      <div className="flex-1">{children}</div>
    </div>
  );
}

function Chip({ children }) {
  return <span className="inline-block bg-slate-100 text-slate-700 font-mono text-[10px] uppercase tracking-widest px-2 py-0.5 rounded">{children}</span>;
}

/* ============ INSPECT PANEL — measured MP4 properties ============ */
function InspectPanel({ contentId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  const run = async () => {
    setLoading(true); setErr(null);
    try {
      const { data } = await api.get(`/studio/content/${contentId}/inspect`);
      setData(data);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not inspect");
    } finally { setLoading(false); }
  };

  return (
    <div className="rounded-xl border border-slate-200 p-4">
      <div className="flex items-center justify-between">
        <div className="font-mono text-[10px] uppercase tracking-widest text-slate-500">// Technical inspection (measured, not claimed)</div>
        <Button data-testid="detail-inspect" onClick={run} disabled={loading} size="sm" variant="outline" className="h-8">
          {loading ? "Measuring…" : data ? "Re-inspect" : "Inspect MP4"}
        </Button>
      </div>
      {err && <div className="mt-2 text-xs text-red-700">{err}</div>}
      {data && data.status === "ok" && (
        <div className="mt-3 grid grid-cols-2 gap-x-4 gap-y-1.5 text-xs font-mono">
          <Row label="Size" value={`${data.size_mb} MB`} />
          <Row label="Duration" value={`${data.duration_s}s / target ${data.target_duration_s}s`} warn={!data.checks.duration_matches_target} />
          <Row label="Resolution" value={data.resolution} />
          <Row label="Video codec" value={`${data.video_codec} · ${data.pix_fmt} · ${data.fps}fps`} />
          <Row label="Audio codec" value={data.audio_codec ? `${data.audio_codec} · ${data.audio_sample_rate_hz}Hz ${data.audio_channels}` : <span className="text-amber-700">missing</span>} warn={!data.checks.has_audio_track} />
          <Row label="Bitrate" value={`${data.bitrate_kbps} kbps`} />
          <Row label="Style preset" value={data.style_preset} />
          <Row label="Voice" value={data.voice || "—"} />
          <Row label="Vertical 9:16" value={data.checks.is_vertical_9_16 ? "yes" : <span className="text-amber-700">no</span>} />
          <Row label="Loudness" value={data.audio_mean_volume_db != null ? `${data.audio_mean_volume_db} dB mean` : "—"} />
          <Row label="Audio audible" value={data.checks.audio_is_audible ? "yes" : <span className="text-amber-700">no — silent</span>} warn={!data.checks.audio_is_audible} />
          <Row label="Speech fits" value={data.checks.speech_fits_all_scenes ? "all scenes" : <span className="text-amber-700">overflow</span>} warn={!data.checks.speech_fits_all_scenes} />
          <Row label="All checks" value={data.all_checks_pass ? <span className="text-emerald-700">pass</span> : <span className="text-amber-700">review required</span>} />
        </div>
      )}
      {data?.scene_qa?.length > 0 && (
        <div data-testid="inspect-scene-qa" className="mt-3 border-t border-slate-200 pt-2 space-y-1 text-[11px] font-mono text-slate-600">
          {data.scene_qa.map((q) => (
            <div key={q.scene} data-testid={`inspect-scene-qa-${q.scene}`} className="flex justify-between gap-2">
              <span>Scene {q.scene} · {q.type === "edu_diagram" ? "diagram" : "video"} · {q.audio}</span>
              <span className={q.fits === false ? "text-amber-700" : "text-emerald-700"}>
                {q.spoken_s != null ? `${q.spoken_s}s / ${q.slot_s}s · ${q.tempo}x` : "—"}
              </span>
            </div>
          ))}
        </div>
      )}
      {data && data.status !== "ok" && (
        <div className="mt-2 text-xs text-amber-700">{data.status}: {data.detail || data.video_url || "no file"}</div>
      )}
    </div>
  );
}

function Row({ label, value, warn }) {
  return (
    <>
      <div className="text-slate-500">{label}</div>
      <div className={warn ? "text-amber-700 font-semibold" : "text-slate-900"}>{value}</div>
    </>
  );
}

/* ============ REEL DETAIL ============ */
function ReelDetail({ content, onBack, qc }) {
  const [c, setC] = useState(content);
  const [rendering, setRendering] = useState(false);
  const [brokenImgs, setBrokenImgs] = useState({});
  const [repairing, setRepairing] = useState(false);
  const repairImages = async () => {
    setRepairing(true);
    try {
      const { data } = await api.post(`/studio/content/${c.id}/repair-images`);
      const { data: fresh } = await api.get(`/studio/content/${c.id}`);
      setC(fresh);
      setBrokenImgs({});
      toast.success(data.repaired_scenes.length ? `Repaired scene image(s): ${data.repaired_scenes.join(", ")}` : "All images are available");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Repair failed");
    } finally {
      setRepairing(false);
    }
  };
  const saveCta = async (v) => {
    if ((v || "").trim() === (c.cta_text || "")) return;
    try {
      const { data } = await api.patch(`/studio/content/${c.id}/settings`, { cta_text: v });
      setC((x) => ({ ...x, cta_text: data.cta_text }));
      toast.success("End-card CTA saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save CTA");
    }
  };
  const isEdu = (c.scenes || []).length > 0 && c.scenes.every((s) => s.scene_type === "edu_diagram");

  // Poll while rendering
  React.useEffect(() => {
    if (c.video_render_status !== "rendering") return;
    const t = setInterval(async () => {
      try {
        const { data } = await api.get(`/studio/content/${c.id}`);
        setC(data);
        if (data.video_render_status !== "rendering") {
          clearInterval(t);
          qc.invalidateQueries({ queryKey: ["content"] });
          if (["ready", "completed"].includes(data.video_render_status)) toast.success("Reel is ready");
          if (data.video_render_status === "failed") toast.error(data.video_render_error || "Render failed");
        }
      } catch (e) { /* keep polling */ }
    }, 4000);
    return () => clearInterval(t);
  }, [c.id, c.video_render_status, qc]);

  const render = async () => {
    if (c.format === "story_film" && !window.confirm(
      "This starts paid generation (1 character sheet, 4 keyframes, 4 Seedance clips, narration) and charges the normal render tokens. Re-renders reuse cached assets. Continue?"
    )) return;
    setRendering(true);
    try {
      await api.post(`/studio/content/${c.id}/render-video`);
      const { data } = await api.get(`/studio/content/${c.id}`);
      setC(data);
      qc.invalidateQueries({ queryKey: ["content"] });
      qc.invalidateQueries({ queryKey: ["wallet-balance"] });
      toast.success(isEdu ? "Render kicked off — diagram reels take about 30 seconds" : "Render kicked off — this takes 15-25 min");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start render");
    } finally {
      setRendering(false);
    }
  };

  const videoAbs = c.video_url ? (c.video_url.startsWith("http") ? c.video_url : `${BACKEND_URL}${c.video_url}`) : null;
  const shareUrl = `${BACKEND_URL}/api/share/${c.id}`;

  return (
    <div className="min-h-screen bg-white text-slate-900">
      <div className="border-b border-slate-200 px-8 py-5 flex items-center gap-3">
        <Button data-testid="detail-back" variant="ghost" onClick={onBack} className="text-slate-600 hover:text-slate-900">
          <ArrowLeft className="w-4 h-4 mr-2" /> Back
        </Button>
        <div className="flex-1">
          <div className="font-mono text-[10px] uppercase tracking-widest text-slate-500">// Reel</div>
          <h1 className="font-display font-bold text-xl text-slate-900">{c.title}</h1>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 p-8">
        {/* Left: player / render controls */}
        <div className="lg:col-span-5 space-y-4">
          <div className="rounded-xl overflow-hidden border border-slate-200 bg-slate-950 aspect-[9/16] max-h-[78vh] mx-auto relative">
            {videoAbs ? (
              <video src={videoAbs} poster={c.thumbnail_url || undefined} controls className="w-full h-full object-contain" data-testid="detail-video" />
            ) : c.thumbnail_url ? (
              <img src={c.thumbnail_url} alt={c.title} className={`w-full h-full object-cover ${isEdu ? "" : "opacity-60"}`} />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-slate-500"><Film className="w-10 h-10" /></div>
            )}
            {!videoAbs && c.video_render_status !== "rendering" && (
              <div className="absolute inset-0 flex items-end justify-center pb-6">
                <Button data-testid="detail-render" onClick={render} disabled={rendering} className="bg-blue-600 hover:bg-blue-700 text-white h-11 px-5 rounded-md shadow-lg">
                  {rendering
                    ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Queuing…</>
                    : <><Rocket className="w-4 h-4 mr-2" />{isEdu ? "Render final video (diagrams + narration)" : c.format === "ugc" ? "Render UGC video (paid: lip-synced talking clips)" : c.format === "story_film" ? "Render story film (paid: keyframes + Seedance)" : "Render final video (Fal Seedance Pro)"}</>}
                </Button>
              </div>
            )}
            {c.video_render_status === "rendering" && (
              <div className="absolute inset-0 bg-black/60 flex flex-col items-center justify-center text-white gap-2">
                <Loader2 className="w-8 h-8 animate-spin" />
                <div className="font-mono text-xs uppercase tracking-widest">{c.video_render_stage || "rendering"} · {Math.round((c.video_render_progress || 0) * 100)}%</div>
              </div>
            )}
          </div>

          {c.video_render_status === "failed" && (
            <div className="rounded-md border border-red-200 bg-red-50 p-3 text-xs text-red-800 flex items-start gap-2">
              <RotateCw className="w-4 h-4 shrink-0 mt-0.5" />
              <div className="flex-1">
                <div className="font-semibold">Render failed</div>
                <div className="mt-0.5">{c.video_render_error || "Try again."}</div>
                {c.tokens_charged ? (
                  c.refunded ? (
                    <div className="mt-1 text-[11px] text-emerald-700 font-mono">
                      ✓ {c.refund_amount || c.tokens_charged} tokens refunded
                      {c.refund_ledger_id && (
                        <span className="opacity-60"> · ledger {String(c.refund_ledger_id).slice(0, 8)}</span>
                      )}
                    </div>
                  ) : (
                    <div className="mt-1 text-[11px] text-amber-700 font-mono">
                      ⚠ {c.tokens_charged} tokens charged · refund pending — contact support if not credited within an hour
                    </div>
                  )
                ) : null}
                <Button data-testid="detail-retry" onClick={render} disabled={rendering} size="sm" className="mt-2 bg-red-600 hover:bg-red-700 text-white h-8">
                  Retry render
                </Button>
              </div>
            </div>
          )}

          {videoAbs && (
            <div className="grid grid-cols-2 gap-2">
              <a data-testid="detail-download" href={videoAbs} download className="h-10 flex items-center justify-center gap-1.5 border border-slate-200 hover:border-blue-400 hover:text-blue-700 rounded-md text-sm text-slate-700 transition-colors">
                <Download className="w-4 h-4" /> Download MP4
              </a>
              <a data-testid="detail-share" href={shareUrl} target="_blank" rel="noopener noreferrer" className="h-10 flex items-center justify-center gap-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-md text-sm">
                <Share2 className="w-4 h-4" /> Public share link
              </a>
            </div>
          )}

          {videoAbs && c.video_render_status === "completed" && (
            <button
              data-testid="detail-rerender"
              onClick={() => window.confirm("Render this reel again? This charges the normal render tokens; unchanged scenes reuse saved clips.") && render()}
              disabled={rendering}
              className="text-xs text-slate-500 hover:text-blue-700 underline"
            >
              Re-render this reel
            </button>
          )}
          {videoAbs && <InspectPanel contentId={c.id} />}
          {c.format === "story_film" && <StoryAssetsPanel content={c} />}
        </div>

        {/* Right: metadata + storyboard */}
        <div className="lg:col-span-7 space-y-4">
          <div className="rounded-xl border border-slate-200 p-5">
            <div className="font-mono text-[10px] uppercase tracking-widest text-slate-500">// Story</div>
            <div className="text-sm text-slate-700 mt-1">{c.hook}</div>
            <div className="text-xs text-slate-500 mt-2 italic">{c.story_spine}</div>
          </div>

          <div className="rounded-xl border border-slate-200 divide-y divide-slate-100">
            <div className="px-4 py-2.5 flex items-center justify-between">
              <div className="font-mono text-[10px] uppercase tracking-widest text-slate-500">// {c.scenes?.length || 0} scenes</div>
              <div className="flex items-center gap-2">
                {Object.keys(brokenImgs).length > 0 && (
                  <button data-testid="repair-images-btn" onClick={repairImages} disabled={repairing} className="text-[11px] text-blue-700 hover:underline disabled:opacity-50">
                    {repairing ? "Repairing…" : "Repair missing images"}
                  </button>
                )}
                <Chip>{c.duration_target_sec}s</Chip>
              </div>
            </div>
            <div className="px-4 py-2.5 flex items-center gap-2">
              <span className="font-mono text-[10px] uppercase tracking-widest text-slate-500 shrink-0">End-card CTA</span>
              <input
                data-testid="cta-text-input"
                defaultValue={c.cta_text || ""}
                placeholder="e.g. HearClear.in / +91 99719 25446"
                onBlur={(e) => saveCta(e.target.value)}
                className="flex-1 text-sm border border-slate-200 rounded px-2 py-1"
              />
            </div>
            {(c.scenes || []).map((s, pos) => (
              <div key={pos} data-testid={`scene-${pos}`} className="px-4 py-3 flex gap-3">
                <div className="w-20 h-28 rounded-md overflow-hidden bg-slate-100 shrink-0">
                  {s.image_url && !brokenImgs[pos] ? (
                    <img src={s.image_url} alt="" className="w-full h-full object-cover" onError={() => setBrokenImgs((b) => ({ ...b, [pos]: true }))} />
                  ) : (
                    <div data-testid={`scene-img-missing-${pos}`} className="w-full h-full flex items-center justify-center text-slate-300"><ImageIcon className="w-5 h-5" /></div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <div className="font-mono text-[10px] uppercase tracking-widest text-blue-600">Scene {pos + 1}</div>
                    <Chip>{s.purpose}</Chip>
                    {s.scene_type ? <Chip>{s.scene_type.replace("_", " ")}</Chip> : <Chip>{s.shot}</Chip>}
                    {s.voice && <Chip>{s.speaker ? `${s.speaker} · ${s.voice}` : s.voice}</Chip>}
                    <Chip>{Number(s.duration_sec || 0).toFixed(1).replace(/\.0$/, "")}s</Chip>
                  </div>
                  {s.dialogue && (
                    <div className="mt-1.5 text-sm text-slate-900">
                      <span className="text-slate-500 text-xs">{s.character_name}:</span> "{s.dialogue}"
                    </div>
                  )}
                  {s.voice_over && !s.dialogue && (
                    <div data-testid={`scene-line-${pos}`} className="mt-1.5 text-sm text-slate-700 italic">{s.subtitle || s.voice_over}</div>
                  )}
                  {s.subtitle && s.subtitle !== s.voice_over && (
                    <div className="mt-0.5 text-[11px] text-slate-400">Spoken as: {s.voice_over}</div>
                  )}
                  {s.on_screen_text && (
                    <div className="mt-1 font-mono text-[10px] uppercase tracking-widest text-slate-400 truncate">TXT: {s.on_screen_text}</div>
                  )}
                </div>
              </div>
            ))}
          </div>

          {(c.caption || (c.hashtags && c.hashtags.length > 0)) && (
            <div className="rounded-xl border border-slate-200 p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="font-mono text-[10px] uppercase tracking-widest text-slate-500">// Caption + hashtags</div>
                <button
                  data-testid="copy-caption"
                  onClick={() => {
                    const txt = [c.caption, (c.hashtags || []).join(" ")].filter(Boolean).join("\n\n");
                    navigator.clipboard.writeText(txt);
                    toast.success("Copied to clipboard");
                  }}
                  className="text-xs text-blue-600 hover:underline font-medium"
                >
                  Copy all
                </button>
              </div>
              <div className="text-sm text-slate-800 whitespace-pre-wrap">{c.caption}</div>
              <div className="mt-2 flex flex-wrap gap-1.5">
                {(c.hashtags || []).map((h) => <Chip key={h}>{h}</Chip>)}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
